import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import type { UpdateViewRequest } from "../ViewRecord";

import { viewQueryKeys } from "./viewQueryKeys";

export function useUpdateView() {
  const { viewService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: { viewId: string; updates: UpdateViewRequest }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      return viewService.updateView(
        request.viewId,
        request.updates,
        currentOrganization.org_id,
      );
    },
    // Captured when the mutation starts, not when it settles: `onSuccess`
    // reads whatever org is current at completion, and a request that spans an
    // org switch would otherwise invalidate the org the user moved to.
    onMutate: () => ({ orgId: currentOrganization?.org_id }),
    onSuccess: (_, { viewId }, { orgId }) => {
      void queryClient.invalidateQueries({
        queryKey: viewQueryKeys.lists(orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: viewQueryKeys.id(orgId, viewId),
      });
    },
  });
}
